import { useState } from 'react';
import { format } from 'date-fns';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmptyState,
} from '@/shared/components/ui/Table';
import { Badge } from '@/shared/components/ui/Badge';
import { Button } from '@/shared/components/ui/Button';
import { BackdatedIndicator } from '@/shared/components/ui/BackdatedIndicator';
import { isOrderBackdated } from '@/shared/utils/dateUtils';
import type { FrameReplenishmentItem } from '@/shared/types/domain.types';

interface FramesTableProps {
  frames: FrameReplenishmentItem[];
  onEdit: (frame: FrameReplenishmentItem) => void;
  isLoading?: boolean;
}

type SortField =
  | 'reorder_entry_date'
  | 'manufacturer'
  | 'brand'
  | 'order_type'
  | 'location';
type SortDirection = 'asc' | 'desc';

export function FramesTable({ frames, onEdit, isLoading }: FramesTableProps) {
  const [sortField, setSortField] = useState<SortField>('reorder_entry_date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortDirection = (field: SortField): 'asc' | 'desc' | null => {
    return sortField === field ? sortDirection : null;
  };

  // Sort frames
  const sortedFrames = [...frames].sort((a, b) => {
    let aValue: string | null | undefined;
    let bValue: string | null | undefined;

    switch (sortField) {
      case 'reorder_entry_date':
        aValue = a.reorder_entry_date;
        bValue = b.reorder_entry_date;
        break;
      case 'manufacturer':
        aValue = a.manufacturer?.name || '';
        bValue = b.manufacturer?.name || '';
        break;
      case 'brand':
        aValue = a.brand;
        bValue = b.brand;
        break;
      case 'order_type':
        aValue = a.order_type;
        bValue = b.order_type;
        break;
      case 'location':
        aValue = a.location?.name || '';
        bValue = b.location?.name || '';
        break;
      default:
        aValue = a.reorder_entry_date;
        bValue = b.reorder_entry_date;
    }

    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // Check if we're displaying ordered/received frames
  const hasOrderedFrames = frames.some((f) => f.status === 'ORDERED' || f.status === 'RECEIVED');

  if (isLoading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <p className="mt-2 text-sm text-gray-600">Loading frames...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="text-sm text-gray-600">
        Showing {frames.length} {frames.length === 1 ? 'frame' : 'frames'}
      </div>

      <Table>
        <TableHeader>
          <tr>
            <TableHead
              sortable
              sortDirection={getSortDirection('reorder_entry_date')}
              onSort={() => handleSort('reorder_entry_date')}
              className="px-2"
            >
              Entry Date
            </TableHead>
            <TableHead
              sortable
              sortDirection={getSortDirection('location')}
              onSort={() => handleSort('location')}
            >
              Location
            </TableHead>
            <TableHead
              sortable
              sortDirection={getSortDirection('manufacturer')}
              onSort={() => handleSort('manufacturer')}
            >
              <span className="hidden lg:inline">Manufacturer</span>
              <span className="lg:hidden">Mfr</span>
            </TableHead>
            <TableHead
              sortable
              sortDirection={getSortDirection('brand')}
              onSort={() => handleSort('brand')}
            >
              Brand
            </TableHead>
            <TableHead>
              <span className="hidden sm:inline">Frame Name/Model</span>
              <span className="sm:hidden">Frame</span>
            </TableHead>
            <TableHead>Color</TableHead>
            <TableHead>Size</TableHead>
            <TableHead
              sortable
              sortDirection={getSortDirection('order_type')}
              onSort={() => handleSort('order_type')}
            >
              Type
            </TableHead>
            <TableHead>
              <span className="hidden md:inline">Patient / Tray</span>
              <span className="md:hidden">P / T</span>
            </TableHead>
            {hasOrderedFrames && (
              <TableHead>
                <span className="hidden md:inline">PO Number</span>
                <span className="md:hidden">PO #</span>
              </TableHead>
            )}
            {hasOrderedFrames && (
              <TableHead>
                <span className="hidden md:inline">Ordered Date</span>
                <span className="md:hidden">Ordered</span>
              </TableHead>
            )}
            <TableHead>Actions</TableHead>
          </tr>
        </TableHeader>
        <TableBody>
          {sortedFrames.length === 0 ? (
            <TableEmptyState
              message="No pending frames. Start by adding a frame to order."
              colSpan={10}
            />
          ) : (
            sortedFrames.map((frame) => (
              <TableRow key={frame.id}>
                <TableCell className="px-2">
                  {format(new Date(frame.reorder_entry_date), 'MM/dd/yyyy')}
                </TableCell>
                <TableCell>
                  <div className="text-sm">
                    <div className="font-medium hidden lg:block">{frame.location?.name}</div>
                    <div className="font-medium lg:hidden">{frame.location?.short_code || frame.location?.name}</div>
                    <div className="text-gray-500 hidden lg:block">{frame.location?.short_code}</div>
                  </div>
                </TableCell>
                <TableCell>{frame.manufacturer?.name}</TableCell>
                <TableCell>{frame.brand}</TableCell>
                <TableCell>
                  <div className="max-w-xs">
                    <div className="font-medium truncate">{frame.frame_name_or_model}</div>
                    {frame.notes && (
                      <div
                        className="text-xs text-gray-500 truncate"
                        title={frame.notes}
                      >
                        {frame.notes}
                      </div>
                    )}
                  </div>
                </TableCell>
                <TableCell>{frame.color}</TableCell>
                <TableCell>{frame.eye_size}</TableCell>
                <TableCell>
                  <Badge variant={frame.order_type === 'STOCK' ? 'blue' : 'purple'}>
                    {frame.order_type}
                  </Badge>
                </TableCell>
                <TableCell>
                  {frame.order_type === 'PATIENT' && (
                    <div className="text-sm">
                      <div className="font-medium">{frame.patient_name}</div>
                      <div className="text-gray-500">Tray: {frame.tray_number}</div>
                    </div>
                  )}
                </TableCell>
                {hasOrderedFrames && (
                  <TableCell>
                    {frame.po_number ? (
                      <span className="text-sm font-medium">{frame.po_number}</span>
                    ) : (
                      <span className="text-sm text-gray-400">-</span>
                    )}
                  </TableCell>
                )}
                {hasOrderedFrames && (
                  <TableCell>
                    {frame.ordered_date ? (
                      <div className="flex items-center gap-2">
                        <span className="text-sm">
                          {format(new Date(frame.ordered_date), 'MM/dd/yyyy')}
                        </span>
                        <BackdatedIndicator
                          isBackdated={isOrderBackdated(frame.ordered_date, frame.created_at)}
                        />
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400">-</span>
                    )}
                  </TableCell>
                )}
                <TableCell>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onEdit(frame)}
                    disabled={frame.status !== 'PENDING'}
                  >
                    Edit
                  </Button>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}
